import { createFileRoute, Link } from "@tanstack/react-router";
import { BUSINESS } from "@/lib/business";
import { CTABand } from "@/components/site/CTABand";
import { TrustBar } from "@/components/site/TrustBar";
import { PhoneButton } from "@/components/site/PhoneButton";
import aboutHero from "@/assets/about-hero.webp";
import aboutTeam from "@/assets/about-team.webp";
import aboutShop from "@/assets/about-shop.webp";
import aboutCustomer from "@/assets/about-customer.webp";
import aboutCustomer1024 from "@/assets/about-customer-1024.webp";
import aboutCustomer640 from "@/assets/about-customer-640.webp";
import aboutShop1024 from "@/assets/about-shop-1024.webp";
import aboutShop640 from "@/assets/about-shop-640.webp";
import aboutTeam1024 from "@/assets/about-team-1024.webp";
import aboutTeam640 from "@/assets/about-team-640.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Phoenix Plumbing Co. | Family-Owned Phoenix Plumbers" },
      { name: "description", content: "Meet the licensed family-owned team behind Phoenix Plumbing Co. Serving the Valley with honest, upfront pricing since 2012. Call 602-834-1208." },
      { property: "og:title", content: "About Phoenix Plumbing Co." },
      { property: "og:description", content: "Family-owned Phoenix plumber since 2012." },
      { property: "og:url", content: "https://www.phoenixplumbing.co/about" },
      { name: "twitter:title", content: "About Phoenix Plumbing Co." },
      { name: "twitter:description", content: "Family-owned Phoenix plumber since 2012." },
    ],
    links: [{ rel: "canonical", href: "https://www.phoenixplumbing.co/about" }],
  }),
  component: About,
});

function About() {
  return (
    <>
      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${aboutHero})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/50" />
        <div className="relative mx-auto max-w-4xl px-4 py-14">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">About Phoenix Plumbing Co.</h1>
          <p className="mt-4 text-lg text-secondary-foreground/90">
            We're a small, family-owned plumbing company serving the Valley since {BUSINESS.founded}. Three generations of Phoenix plumbers, one number on your fridge.
          </p>
        </div>
      </section>
      <div className="mx-auto max-w-5xl px-4 mt-8">
        <img srcSet={`${aboutTeam640} 640w, ${aboutTeam1024} 1024w, ${aboutTeam} 1264w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1264} height={848} decoding="async" src={aboutTeam} alt="Phoenix Plumbing Co. family-owned team in front of their fleet" className="w-full h-auto rounded-2xl shadow-2xl ring-1 ring-border" loading="lazy" />
      </div>
      <article className="mx-auto max-w-3xl px-4 py-12 prose prose-lg">
        <p>
          Phoenix Plumbing Co. started in {BUSINESS.founded} with a single truck and a simple promise: show up when you say you will, quote the job before you touch it, and stand behind every wrench-turn.
        </p>
        <h2>Family-owned and locally run</h2>
        <p>
          We live in the Valley. We answer our own phones. The plumber knocking on your door is on our payroll — not a subcontractor we just dispatched from a call center.
        </p>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border my-8 not-prose">
          <img srcSet={`${aboutShop640} 640w, ${aboutShop1024} 1024w, ${aboutShop} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1280} height={832} decoding="async"
            src={aboutShop}
            alt="Phoenix Plumbing Co. supply warehouse stocked with parts"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <h2>Licensed, bonded & insured</h2>
        <p>
          {BUSINESS.license}. We carry $2M general liability and full workers' comp. Every technician is background-checked, drug-tested, and trained by us.
        </p>
        <h2>Honest, upfront pricing</h2>
        <p>
          You see the flat price before any work begins. Repairs, installs, emergencies — same approach every time.
        </p>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border my-8 not-prose">
          <img srcSet={`${aboutCustomer640} 640w, ${aboutCustomer1024} 1024w, ${aboutCustomer} 1264w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1264} height={848} decoding="async"
            src={aboutCustomer}
            alt="Phoenix homeowner thanking our plumber on her front porch"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <h2>Workmanship guarantee</h2>
        <p>
          Every repair and install is backed by a written workmanship warranty. If something we did fails, we come back and fix it.
        </p>
        <h2>Awards and accreditations</h2>
        <ul>
          <li>Arizona ROC Licensed, Bonded & Insured</li>
          <li>Family-Owned Since {BUSINESS.founded}</li>
          <li>Trusted by thousands of Arizona homeowners since 2012</li>
          <li>Written workmanship warranty on every job</li>
        </ul>
        <h2>Licensed plumbers lead our work</h2>
        <p>
          Licensed plumbers lead our plumbing work in Phoenix. Apprentices and helpers
          work under licensed supervision when needed. We hire slowly, train constantly,
          and back every job with a written workmanship warranty.
        </p>
      </article>
      <div className="mx-auto max-w-7xl px-4 pb-12"><TrustBar /></div>
      <div className="mx-auto max-w-3xl px-4 pb-14 text-center">
        <PhoneButton size="lg" />
        <p className="mt-3 text-muted-foreground">Or <Link to="/contact" className="text-primary underline font-semibold">request service online →</Link></p>
      </div>
      <CTABand />
    </>
  );
}